import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Edit2, Save, X, Send, BookOpen, Plus, Trash2, Archive, ArchiveRestore, AlertCircle, CheckCircle, Users, Sparkles, Loader2 } from 'lucide-react';
import Header from './Header';
import { formatDate, formatDateTime } from '../utils/dateFormatter';

// Using relative URLs

const TrainingDetail = () => {
  const { trainingId } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [completions, setCompletions] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    duration_minutes: 15,
    questions: []
  });
  
  // Dialog states
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [showUnpublishDialog, setShowUnpublishDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showUnarchiveDialog, setShowUnarchiveDialog] = useState(false);
  const [showPublishAgainDialog, setShowPublishAgainDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteQuestionDialog, setShowDeleteQuestionDialog] = useState(false);
  const [showCompletionsDialog, setShowCompletionsDialog] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [confirmationText, setConfirmationText] = useState('');

  // AI add-more-questions dialog
  const [showAiQuestionDialog, setShowAiQuestionDialog] = useState(false);
  const [aiCount, setAiCount] = useState(5);
  const [aiTopic, setAiTopic] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiPreviewQuestions, setAiPreviewQuestions] = useState(null);

  useEffect(() => {
    fetchTraining();
    fetchCompletions();
  }, [trainingId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchTraining = async () => {
    try {
      const response = await axios.get(
        `/api/trainings/${trainingId}`,
        getAuthHeaders()
      );
      setTraining(response.data);
      setFormData({
        title: response.data.title,
        description: response.data.description || '',
        content: response.data.content || '',
        duration_minutes: response.data.duration_minutes || 15,
        questions: response.data.questions || []
      });
    } catch (error) {
      toast.error('Failed to load training');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletions = async () => {
    try {
      const response = await axios.get(
        `/api/trainings/${trainingId}/completions`,
        getAuthHeaders()
      );
      setCompletions(response.data);
    } catch (error) {
      // Silently fail if user doesn't have permission
      console.log('Could not fetch completions:', error);
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(
        `/api/trainings/${trainingId}`,
        formData,
        getAuthHeaders()
      );
      toast.success('Training updated successfully!');
      setEditing(false);
      fetchTraining();
    } catch (error) {
      toast.error('Failed to update training');
      console.error(error);
    }
  };

  const handlePublish = async (resetCompletions = false) => {
    if (!formData.content || !formData.questions || formData.questions.length === 0) {
      toast.error('Please add training content and questions before publishing');
      return;
    }

    try {
      await axios.post(
        `/api/trainings/${trainingId}/publish?reset_completions=${resetCompletions}`,
        {},
        getAuthHeaders()
      );
      toast.success(resetCompletions ? 'Training re-published! Previous completions cleared.' : 'Training published! Employees can now take it.');
      setShowPublishDialog(false);
      setShowPublishAgainDialog(false);
      fetchTraining();
      fetchCompletions();
    } catch (error) {
      toast.error('Failed to publish training');
      console.error(error);
    }
  };

  const handleUnpublish = async () => {
    if (confirmationText !== 'UNPUBLISH') {
      toast.error('Please type UNPUBLISH to confirm');
      return;
    }

    try {
      await axios.post(
        `/api/trainings/${trainingId}/unpublish`,
        {},
        getAuthHeaders()
      );
      toast.success('Training unpublished. You can now edit and re-publish.');
      setShowUnpublishDialog(false);
      setConfirmationText('');
      fetchTraining();
      fetchCompletions();
    } catch (error) {
      toast.error('Failed to unpublish training');
      console.error(error);
    }
  };

  const handleArchive = async () => {
    try {
      await axios.post(
        `/api/trainings/${trainingId}/archive`,
        {},
        getAuthHeaders()
      );
      toast.success('Training archived successfully');
      setShowArchiveDialog(false);
      fetchTraining();
    } catch (error) {
      toast.error('Failed to archive training');
      console.error(error);
    }
  };

  const handleUnarchive = async () => {
    try {
      await axios.post(
        `/api/trainings/${trainingId}/unarchive`,
        {},
        getAuthHeaders()
      );
      toast.success('Training unarchived successfully');
      setShowUnarchiveDialog(false);
      fetchTraining();
    } catch (error) {
      toast.error('Failed to unarchive training');
      console.error(error);
    }
  };

  const isAllCompleted = () => {
    if (!completions || !completions.total_employees) return false;
    return completions.completed_count === completions.total_employees && completions.total_employees > 0;
  };

  const handleDelete = async () => {
    if (confirmationText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    try {
      await axios.delete(
        `/api/trainings/${trainingId}`,
        getAuthHeaders()
      );
      toast.success('Training deleted permanently');
      setShowDeleteDialog(false);
      setConfirmationText('');
      navigate('/trainings');
    } catch (error) {
      toast.error('Failed to delete training');
      console.error(error);
    }
  };

  const handleDeleteQuestion = (questionId) => {
    // Show confirmation dialog instead of deleting immediately
    setQuestionToDelete(questionId);
    setShowDeleteQuestionDialog(true);
  };

  const confirmDeleteQuestion = () => {
    if (confirmationText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    const updatedQuestions = formData.questions.filter(q => q.id !== questionToDelete);
    setFormData({ ...formData, questions: updatedQuestions });
    setShowDeleteQuestionDialog(false);
    setQuestionToDelete(null);
    setConfirmationText('');
    toast.success('Question deleted');
  };

  const handleAddQuestion = () => {
    const newQuestion = {
      id: `q${Date.now()}`,
      question: '',
      options: {
        A: '',
        B: '',
        C: '',
        D: ''
      },
      correct_answer: 'A',
      explanation: ''
    };
    setFormData({ 
      ...formData, 
      questions: [...formData.questions, newQuestion] 
    });
  };

  const handleGenerateAiQuestions = async () => {
    setAiGenerating(true);
    setAiPreviewQuestions(null);
    try {
      const resp = await axios.post(
        `/api/trainings/${trainingId}/generate-more-questions`,
        {
          count: Math.max(1, Math.min(25, parseInt(aiCount, 10) || 5)),
          topic_focus: aiTopic.trim() || null,
        },
        getAuthHeaders()
      );
      const items = resp.data?.new_questions || [];
      if (items.length === 0) {
        toast.error('AI did not return any usable questions. Try a different topic.');
      } else {
        setAiPreviewQuestions(items);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'AI generation failed');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAddAiQuestionsToTraining = () => {
    if (!aiPreviewQuestions || aiPreviewQuestions.length === 0) return;
    setFormData({
      ...formData,
      questions: [...formData.questions, ...aiPreviewQuestions],
    });
    toast.success(`Added ${aiPreviewQuestions.length} new question${aiPreviewQuestions.length > 1 ? 's' : ''}`);
    setShowAiQuestionDialog(false);
    setAiPreviewQuestions(null);
    setAiTopic('');
    setAiCount(5);
  };

  const handleQuestionChange = (questionId, field, value) => {
    const updatedQuestions = formData.questions.map(q => {
      if (q.id === questionId) {
        return { ...q, [field]: value };
      }
      return q;
    });
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const handleOptionChange = (questionId, optionKey, value) => {
    const updatedQuestions = formData.questions.map(q => {
      if (q.id === questionId) {
        return {
          ...q,
          options: {
            ...q.options,
            [optionKey]: value
          }
        };
      }
      return q;
    });
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'published':
        return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'archived':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  if (!training) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Training Not Found</h2>
            <Button onClick={() => navigate('/trainings')}>Back to Trainings</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      <Header />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="outline"
          onClick={() => navigate('/trainings')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Trainings
        </Button>

        {/* Training Header Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <BookOpen className="w-6 h-6 text-navy-600" />
                  <Badge className={getStatusBadgeColor(training.status)}>
                    {training.status}
                  </Badge>
                </div>
                
                {editing ? (
                  <div className="space-y-3">
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="text-2xl font-bold"
                      placeholder="Training Title"
                    />
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Training Description"
                      rows={2}
                    />
                  </div>
                ) : (
                  <>
                    <CardTitle className="text-2xl mb-2">{training.title}</CardTitle>
                    {training.description && (
                      <CardDescription className="text-base">
                        {training.description}
                      </CardDescription>
                    )}
                  </>
                )}
              </div>

              <div className="flex gap-2">
                {!editing ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditing(true)}
                      disabled={training.status === 'published' || training.status === 'archived'}
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    {training.status === 'draft' && training.content && training.questions?.length > 0 && (
                      <Button
                        size="sm"
                        onClick={() => setShowPublishDialog(true)}
                        className="bg-navy-600 hover:bg-navy-700"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Publish
                      </Button>
                    )}
                    {training.status === 'published' && !isAllCompleted() && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowUnpublishDialog(true)}
                          className="border-orange-300 text-orange-700 hover:bg-orange-50"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Unpublish
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowCompletionsDialog(true)}
                          className="border-blue-300 text-blue-700 hover:bg-blue-50"
                        >
                          <Users className="w-4 h-4 mr-2" />
                          View Completions
                        </Button>
                      </>
                    )}
                    {training.status === 'published' && isAllCompleted() && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowUnpublishDialog(true)}
                          className="border-orange-300 text-orange-700 hover:bg-orange-50"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Unpublish
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowCompletionsDialog(true)}
                          className="border-blue-300 text-blue-700 hover:bg-blue-50"
                        >
                          <Users className="w-4 h-4 mr-2" />
                          View Completions
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setShowPublishAgainDialog(true)}
                          className="bg-navy-600 hover:bg-navy-700"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Publish Again
                        </Button>
                      </>
                    )}
                    {(training.status === 'draft' || training.status === 'published') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowArchiveDialog(true)}
                        className="border-slate-300 text-slate-700 hover:bg-slate-50"
                      >
                        <Archive className="w-4 h-4 mr-2" />
                        Archive
                      </Button>
                    )}
                    {training.status === 'archived' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowUnarchiveDialog(true)}
                        className="border-emerald-300 text-navy-700 hover:bg-emerald-50"
                      >
                        <ArchiveRestore className="w-4 h-4 mr-2" />
                        Unarchive
                      </Button>
                    )}
                    {/* Delete button - available for all statuses */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDeleteDialog(true)}
                      className="border-red-300 text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditing(false);
                        setFormData({
                          title: training.title,
                          description: training.description || '',
                          content: training.content || '',
                          duration_minutes: training.duration_minutes || 15,
                          questions: training.questions || []
                        });
                      }}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleUpdate}
                      className="bg-navy-600 hover:bg-navy-700"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>

          {/* Completion Stats Banner */}
          {training.completion_stats && training.status === 'published' && (
            <div className="px-6 py-4 bg-navy-50 border-y border-navy-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white rounded-lg p-2">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600 font-medium">Completion Status</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {training.completion_stats.completed_count} of {training.completion_stats.total_employees} completed
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-blue-200">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                    <span className="text-xl font-bold text-blue-600">
                      {Math.round(training.completion_stats.completion_rate)}%
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Completion Rate</p>
                </div>
              </div>
            </div>
          )}

          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              {training.frameworks && training.frameworks.map((fw) => (
                <Badge key={fw} variant="outline">{fw}</Badge>
              ))}
              <div className="flex items-center gap-2">
                <Label className="text-sm text-slate-600">Duration:</Label>
                {editing ? (
                  <Input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                    className="w-20"
                  />
                ) : (
                  <span className="text-sm text-slate-900">{training.duration_minutes} minutes</span>
                )}
              </div>
            </div>
            {training.status === 'published' && !isAllCompleted() && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  ℹ️ This training is currently live. To make changes, click "Unpublish" first, then you can edit and re-publish.
                </p>
              </div>
            )}
            {training.status === 'published' && isAllCompleted() && (
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-navy-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-800 mb-1">
                      All Employees Completed! 🎉
                    </p>
                    <p className="text-sm text-navy-700">
                      All {completions?.total_employees} employees have completed this training. You can "Publish Again" to reset completions and allow employees to retake it.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {training.status === 'archived' && (
              <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <p className="text-sm text-slate-700">
                  📦 This training is archived. Click "Unarchive" to restore it to draft status.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Training Content Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Training Content</CardTitle>
            <CardDescription>Training material that employees will read</CardDescription>
          </CardHeader>
          
          <CardContent>
            {editing ? (
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Training content..."
                rows={15}
                className="font-mono text-sm"
              />
            ) : (
              <div className="prose max-w-none">
                {training.content ? (
                  <div className="whitespace-pre-wrap text-slate-700">
                    {training.content}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>No content yet. Click Edit to add training content.</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quiz Questions Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Quiz Questions ({formData.questions.length})</CardTitle>
                <CardDescription>Employees must answer these to complete the training</CardDescription>
              </div>
              {editing && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAiQuestionDialog(true)}
                    className="border-violet-300 text-violet-700 hover:bg-violet-50"
                    data-testid="ai-add-questions-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Add with AI
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddQuestion}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Question
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {formData.questions.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <p>No questions yet. {editing && 'Click "Add Question" to create one.'}</p>
              </div>
            ) : (
              formData.questions.map((question, index) => (
                <div key={question.id} className="p-4 border rounded-lg bg-slate-50">
                  {editing ? (
                    <div className="space-y-4">
                      <div className="flex items-start justify-between">
                        <Label className="text-sm font-medium">Question {index + 1}</Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteQuestion(question.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <Textarea
                        value={question.question}
                        onChange={(e) => handleQuestionChange(question.id, 'question', e.target.value)}
                        placeholder="Enter question"
                        rows={2}
                      />

                      <div className="grid grid-cols-1 gap-2">
                        {Object.entries(question.options).map(([key, value]) => (
                          <div key={key} className="flex items-center gap-2">
                            <Label className="w-8">{key}.</Label>
                            <Input
                              value={value}
                              onChange={(e) => handleOptionChange(question.id, key, e.target.value)}
                              placeholder={`Option ${key}`}
                            />
                          </div>
                        ))}
                      </div>

                      <div className="flex items-center gap-4">
                        <Label className="text-sm">Correct Answer:</Label>
                        <select
                          value={question.correct_answer}
                          onChange={(e) => handleQuestionChange(question.id, 'correct_answer', e.target.value)}
                          className="px-3 py-1 border border-slate-300 rounded-lg"
                        >
                          <option value="A">A</option>
                          <option value="B">B</option>
                          <option value="C">C</option>
                          <option value="D">D</option>
                        </select>
                      </div>

                      <Textarea
                        value={question.explanation || ''}
                        onChange={(e) => handleQuestionChange(question.id, 'explanation', e.target.value)}
                        placeholder="Explanation (optional)"
                        rows={2}
                      />
                    </div>
                  ) : (
                    <>
                      <p className="font-semibold text-slate-900 mb-3">
                        {index + 1}. {question.question}
                      </p>
                      <div className="space-y-2 ml-4">
                        {Object.entries(question.options).map(([key, value]) => (
                          <div key={key} className="flex items-start">
                            <span className={`font-medium mr-2 ${key === question.correct_answer ? 'text-navy-600' : 'text-slate-600'}`}>
                              {key}.
                            </span>
                            <span className={key === question.correct_answer ? 'text-navy-600 font-medium' : 'text-slate-700'}>
                              {value} {key === question.correct_answer && '✓'}
                            </span>
                          </div>
                        ))}
                      </div>
                      {question.explanation && (
                        <p className="text-sm text-slate-600 mt-3 ml-4 italic">
                          Explanation: {question.explanation}
                        </p>
                      )}
                    </>
                  )}
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Metadata */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Metadata</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Created</p>
                <p className="font-medium">{formatDateTime(training.created_at)}</p>
              </div>
              {training.published_at && (
                <div>
                  <p className="text-slate-500">Published</p>
                  <p className="font-medium">{formatDateTime(training.published_at)}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Employee Completion Stats */}
        {training.status === 'published' && completions && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg">Employee Completion Status</CardTitle>
              <CardDescription>
                {completions.completed_count} of {completions.total_employees} employees have completed this training
                ({completions.completion_rate.toFixed(1)}%)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="w-full bg-slate-200 rounded-full h-3">
                  <div 
                    className="bg-navy-600 h-3 rounded-full transition-all"
                    style={{width: `${completions.completion_rate}%`}}
                  ></div>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowCompletionsDialog(true)}
                className="w-full"
              >
                <Users className="w-4 h-4 mr-2" />
                View Detailed Completion Report
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Publish Confirmation Dialog */}
      <Dialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Publish Training</DialogTitle>
            <DialogDescription>
              Are you sure you want to publish this training? All employees will be able to see and take this training.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPublishDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => handlePublish(false)} className="bg-navy-600 hover:bg-navy-700">
              Publish Training
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unpublish Confirmation Dialog */}
      <Dialog open={showUnpublishDialog} onOpenChange={(open) => {
        setShowUnpublishDialog(open);
        if (!open) setConfirmationText('');
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertCircle className="w-5 h-5" />
              Confirm Unpublish Training
            </DialogTitle>
            <DialogDescription>
              This will remove the training from all employees' pending lists. Any in-progress attempts will be lost.
              <br /><br />
              Type <strong>UNPUBLISH</strong> to confirm:
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Type UNPUBLISH"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value.toUpperCase())}
            className="mt-2"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowUnpublishDialog(false);
              setConfirmationText('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleUnpublish}
              disabled={confirmationText !== 'UNPUBLISH'}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Unpublish Training
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Again Dialog */}
      <Dialog open={showPublishAgainDialog} onOpenChange={setShowPublishAgainDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish Training Again</DialogTitle>
            <DialogDescription>
              All employees have completed this training. Would you like to publish it again?
              <br /><br />
              This will:
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>Clear all previous completion records</li>
                <li>Allow employees to retake the training</li>
                <li>Keep the training content unchanged</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPublishAgainDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => handlePublish(true)} className="bg-navy-600 hover:bg-navy-700">
              Publish Again & Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Training</DialogTitle>
            <DialogDescription>
              Are you sure you want to archive this training? It will no longer be visible to employees and cannot be taken.
              You can unarchive it later if needed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleArchive} className="bg-slate-600 hover:bg-slate-700">
              Archive Training
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unarchive Confirmation Dialog */}
      <Dialog open={showUnarchiveDialog} onOpenChange={setShowUnarchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unarchive Training</DialogTitle>
            <DialogDescription>
              This will restore the training to draft status. You can then edit and publish it again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnarchiveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUnarchive} className="bg-navy-600 hover:bg-navy-700">
              Unarchive Training
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => {
        setShowDeleteDialog(open);
        if (!open) setConfirmationText('');
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Permanently Delete Training
            </DialogTitle>
            <DialogDescription>
              <strong className="text-red-600">⚠️ This action cannot be undone!</strong>
              <br /><br />
              This will permanently delete:
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>The training and all its content</li>
                <li>All quiz questions</li>
                <li>All employee completion records</li>
              </ul>
              <br />
              Type <strong>DELETE</strong> to confirm:
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Type DELETE"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value.toUpperCase())}
            className="mt-2"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteDialog(false);
              setConfirmationText('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleDelete}
              disabled={confirmationText !== 'DELETE'}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Question Confirmation Dialog */}
      <Dialog open={showDeleteQuestionDialog} onOpenChange={(open) => {
        setShowDeleteQuestionDialog(open);
        if (!open) {
          setConfirmationText('');
          setQuestionToDelete(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Delete Question
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this question? This action cannot be undone.
              <br /><br />
              Type <strong>DELETE</strong> to confirm:
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Type DELETE"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value.toUpperCase())}
            className="mt-2"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteQuestionDialog(false);
              setConfirmationText('');
              setQuestionToDelete(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={confirmDeleteQuestion}
              disabled={confirmationText !== 'DELETE'}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Question
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Completions Dialog */}
      <Dialog open={showCompletionsDialog} onOpenChange={setShowCompletionsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-navy-600" />
              Training Completion Report
            </DialogTitle>
            <DialogDescription>
              Detailed completion tracking for "{training?.title}"
            </DialogDescription>
          </DialogHeader>
          
          {completions && (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription className="text-xs">Total Employees</CardDescription>
                    <CardTitle className="text-2xl">{completions.total_employees}</CardTitle>
                  </CardHeader>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription className="text-xs">Completed</CardDescription>
                    <CardTitle className="text-2xl text-navy-600">{completions.completed_count}</CardTitle>
                  </CardHeader>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription className="text-xs">Completion Rate</CardDescription>
                    <CardTitle className="text-2xl text-blue-600">{completions.completion_rate.toFixed(1)}%</CardTitle>
                  </CardHeader>
                </Card>
              </div>

              {/* Progress Bar */}
              <div>
                <div className="w-full bg-slate-200 rounded-full h-3">
                  <div 
                    className="bg-navy-600 h-3 rounded-full transition-all"
                    style={{width: `${completions.completion_rate}%`}}
                  ></div>
                </div>
              </div>

              {/* Completed Employees List */}
              {completions.completions && completions.completions.length > 0 ? (
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-900 text-sm">Completed By:</h4>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {completions.completions.map((completion) => (
                      <div key={completion.completion_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${completion.passed ? 'bg-emerald-100' : 'bg-orange-100'}`}>
                            {completion.passed ? (
                              <CheckCircle className="w-5 h-5 text-navy-600" />
                            ) : (
                              <AlertCircle className="w-5 h-5 text-orange-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{completion.user_name}</p>
                            <p className="text-xs text-slate-600">{completion.user_email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-semibold ${completion.passed ? 'text-navy-600' : 'text-orange-600'}`}>
                            Score: {completion.score}%
                          </p>
                          <p className="text-xs text-slate-500">
                            {formatDate(completion.completed_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No completions yet</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompletionsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generate More Questions Dialog */}
      <Dialog open={showAiQuestionDialog} onOpenChange={(open) => {
        setShowAiQuestionDialog(open);
        if (!open) {
          setAiPreviewQuestions(null);
          setAiTopic('');
          setAiCount(5);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-questions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
              Add Questions with AI
            </DialogTitle>
            <DialogDescription>
              Generate additional MCQs based on this training's frameworks and existing content.
              The AI is told not to duplicate any question already in the training.
            </DialogDescription>
          </DialogHeader>

          {!aiPreviewQuestions ? (
            <div className="space-y-4 py-2">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  How many questions?
                </label>
                <Input
                  type="number"
                  min={1}
                  max={25}
                  value={aiCount}
                  onChange={(e) => setAiCount(e.target.value)}
                  className="max-w-[140px]"
                  data-testid="ai-questions-count"
                />
                <p className="text-xs text-slate-500 mt-1">Allowed range: 1–25.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Topic focus <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <Textarea
                  value={aiTopic}
                  onChange={(e) => setAiTopic(e.target.value)}
                  placeholder="e.g. incident response procedures, encryption-at-rest scenarios, vendor risk…"
                  rows={3}
                  data-testid="ai-questions-topic"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Leave blank for general coverage of the existing frameworks.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <p className="text-sm text-slate-600">
                {aiPreviewQuestions.length} question{aiPreviewQuestions.length !== 1 ? 's' : ''} generated.
                Review them below — click <b>Add to training</b> to keep them, or <b>Regenerate</b> to try again.
              </p>
              <div className="max-h-96 overflow-y-auto space-y-3 border border-slate-200 rounded-md p-3 bg-slate-50">
                {aiPreviewQuestions.map((q, i) => (
                  <div key={q.id || i} className="rounded-md bg-white border border-slate-200 p-3 text-sm">
                    <p className="font-medium text-slate-900 mb-2">
                      {i + 1}. {q.question}
                    </p>
                    <ul className="space-y-1 text-xs">
                      {Object.entries(q.options || {}).map(([opt, txt]) => (
                        <li
                          key={opt}
                          className={
                            q.correct_answer === opt
                              ? 'text-emerald-700 font-medium'
                              : 'text-slate-600'
                          }
                        >
                          {opt}. {txt}
                          {q.correct_answer === opt && ' ✓'}
                        </li>
                      ))}
                    </ul>
                    {q.explanation && (
                      <p className="text-xs text-slate-500 mt-2 italic">
                        {q.explanation}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAiQuestionDialog(false)}
              disabled={aiGenerating}
            >
              Cancel
            </Button>
            {aiPreviewQuestions ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleGenerateAiQuestions}
                  disabled={aiGenerating}
                >
                  {aiGenerating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Regenerating…</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" />Regenerate</>
                  )}
                </Button>
                <Button
                  onClick={handleAddAiQuestionsToTraining}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="ai-add-to-training-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add to training
                </Button>
              </>
            ) : (
              <Button
                onClick={handleGenerateAiQuestions}
                disabled={aiGenerating}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="ai-generate-btn"
              >
                {aiGenerating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating…</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />Generate</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default TrainingDetail;
